import React from 'react'
import { createRoot } from 'react-dom/client'
import { registerLicense } from '@syncfusion/ej2-base'
import { QueryClient, QueryClientProvider } from 'react-query'
import { createWebStoragePersistor } from 'react-query/createWebStoragePersistor-experimental'
import { persistQueryClient } from 'react-query/persistQueryClient-experimental'
import { Provider } from 'react-redux'
// Ensure CoreUI base styles are loaded
import App from './App.jsx'
import { icons } from './assets/icons'
import './polyfill'
import './util/suppressWarnings'
import { withMsal } from "@azure/msal-react";
import { publicClientApplication } from "./util/authConfig";
import { MsalProvider } from "@azure/msal-react";
import store from './util/store'

const syncfusionLicenseKey = (import.meta.env.VITE_SYNCFUSION_LICENSE_KEY || '').trim()

if (syncfusionLicenseKey) {
  registerLicense(syncfusionLicenseKey)
} else {
  console.warn('Syncfusion license key is missing. Set VITE_SYNCFUSION_LICENSE_KEY to remove trial banner.')
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 1000 * 60 * 60 * 12
    }
  }
})

const localStoragePersistor = createWebStoragePersistor({ storage: window.localStorage })

persistQueryClient({
  queryClient,
  persistor: localStoragePersistor
})

React.icons = icons

const ProfileWithMsal = withMsal(App);

// E2E shim: when VITE_E2E is 'true', stub MSAL behavior so tests can run
// without a real AAD login. This only activates when the environment variable
// is set and is intended for local CI/E2E debugging only.
if (import.meta.env.VITE_E2E === 'true') {
  try {
    // Return a fake authenticated account so useMsal() sees a logged-in user
    publicClientApplication.getAllAccounts = () => [
      { idTokenClaims: { roles: ['InternalUser'] }, username: 'e2e@local', name: 'E2E User' }
    ]
    // Return a trivial access token for any acquireTokenSilent calls
    publicClientApplication.acquireTokenSilent = async () => ({ accessToken: 'e2e-access-token' })
    console.info('MSAL E2E shim enabled')
  } catch (e) {
    console.warn('Failed to enable MSAL E2E shim', e)
  }
}

const container = document.getElementById('root')
const root = createRoot(container)
root.render(
  <MsalProvider instance={publicClientApplication}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <ProfileWithMsal />
        </Provider>
      </QueryClientProvider>
  </MsalProvider>
)
